import { useEffect, useReducer, useState } from 'react';

import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import { SPONSOR_TIER_DURATION } from '../../constants';
import { SponsorLevel, sponsors as sponsorList } from '../../data/sponsors';

interface TickerSponsor {
  tierLabel: string;
  duration: number;
  name: string;
  logoUrl: string;
  site: string;
}

function getTierLabel(level: SponsorLevel) {
  if (level === SponsorLevel.GOLD) return 'Patrocinador Ouro';
  if (level === SponsorLevel.SILVER) return 'Patrocinador Prata';
  return 'Patrocinador Bronze';
}

const tickerSponsors: TickerSponsor[] = sponsorList
  .filter(
    (sponsor) =>
      sponsor.level === SponsorLevel.GOLD ||
      sponsor.level === SponsorLevel.SILVER ||
      sponsor.level === SponsorLevel.BRONZE,
  )
  .map((sponsor) => ({
    tierLabel: getTierLabel(sponsor.level),
    duration: SPONSOR_TIER_DURATION[sponsor.level],
    name: sponsor.name,
    logoUrl: sponsor.imageUrl,
    site: sponsor.site,
  }));

type Phase = 'in' | 'out';
interface CarouselState {
  index: number;
  phase: Phase;
}
type CarouselAction = { type: 'EXIT' } | { type: 'NEXT' };

function carouselReducer(
  state: CarouselState,
  action: CarouselAction,
): CarouselState {
  if (action.type === 'EXIT') return { ...state, phase: 'out' };
  if (tickerSponsors.length === 0) return state;
  if (action.type === 'NEXT')
    return { index: (state.index + 1) % tickerSponsors.length, phase: 'in' };
  return state;
}

interface SponsorTickerProps {
  triggerSectionId: string;
}

const duplicated = [...tickerSponsors, ...tickerSponsors];

export function SponsorTicker({ triggerSectionId }: SponsorTickerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [{ index, phase }, dispatch] = useReducer(carouselReducer, {
    index: 0,
    phase: 'in',
  });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      const section = document.getElementById(triggerSectionId);
      if (!section) return;
      const rect = section.getBoundingClientRect();
      setVisible(rect.top < window.innerHeight * 0.75);
    };

    window.addEventListener('scroll', checkScroll, { passive: true });
    return () => window.removeEventListener('scroll', checkScroll);
  }, [triggerSectionId]);

  useEffect(() => {
    if (isMobile) return;
    if (phase === 'out') {
      const t = setTimeout(() => dispatch({ type: 'NEXT' }), 400);
      return () => clearTimeout(t);
    }
    const duration = tickerSponsors[index]?.duration ?? 3000;
    const t = setTimeout(() => dispatch({ type: 'EXIT' }), duration);
    return () => clearTimeout(t);
  }, [index, phase, isMobile]);

  const current = tickerSponsors[index];

  if (!current) {
    return null;
  }

  if (isMobile) {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          pointerEvents: visible ? 'auto' : 'none',
        }}>
        <Box
          sx={{
            backgroundColor: 'rgba(14, 15, 14, 0.96)',
            backdropFilter: 'blur(16px)',
            borderTop: '1px solid rgba(255,255,255,0.07)',
            height: 56,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 48,
              background:
                'linear-gradient(to right, rgba(14,15,14,0.96), transparent)',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: 48,
              background:
                'linear-gradient(to left, rgba(14,15,14,0.96), transparent)',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0,
              '@keyframes marquee': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(-50%)' },
              },
              animation: 'marquee 40s linear infinite',
              willChange: 'transform',
            }}>
            {duplicated.map((sponsor, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 3,
                  flexShrink: 0,
                }}>
                <Typography
                  fontSize={10}
                  fontWeight={600}
                  letterSpacing={1.2}
                  textTransform="uppercase"
                  color="rgba(255,255,255,0.35)"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {sponsor.tierLabel}
                </Typography>
                <Box
                  component="a"
                  href={sponsor.site}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`acessar site ${sponsor.name}`}
                  sx={{
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    whiteSpace: 'nowrap',
                    px: 1.5,
                    py: 0.65,
                    borderRadius: 0.75,
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    '&:hover': {
                      color: '#F8C495',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                    flexShrink: 0,
                  }}>
                  {sponsor.name.toUpperCase()}
                </Box>
                <Box
                  sx={{
                    width: '1px',
                    height: 20,
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    ml: 1.5,
                    flexShrink: 0,
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        zIndex: 1200,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}>
      <Box
        sx={{
          backgroundColor: 'rgba(18, 18, 18, 0.65)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '14px',
          px: 1,
          py: 1,
          minWidth: 200,
          overflow: 'hidden',
        }}>
        <Box
          key={index}
          sx={{
            '@keyframes tickerEnter': {
              '0%': { opacity: 0, transform: 'translateY(12px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
            '@keyframes tickerExit': {
              '0%': { opacity: 1, transform: 'translateY(0)' },
              '100%': { opacity: 0, transform: 'translateY(-10px)' },
            },
            animationName: phase === 'out' ? 'tickerExit' : 'tickerEnter',
            animationDuration: '0.4s',
            animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            animationFillMode: 'forwards',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}>
          <Typography
            fontSize={11}
            fontWeight={500}
            letterSpacing={1.5}
            textTransform="uppercase"
            color="rgba(255,255,255,0.45)">
            {current.tierLabel}
          </Typography>
          <Box
            component="a"
            href={current.site}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`acessar site ${current.name}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              p: 0.5,
              borderRadius: 1,
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-1px)',
              },
            }}>
            <Box
              component="img"
              src={current.logoUrl}
              alt={current.name}
              sx={{
                height: 48,
                width: 168,
                objectFit: 'contain',
                bgcolor: 'white',
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
